import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Send } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import './Reviews.css';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    rating: 5,
    review_text: '',
    event_type: '',
  });

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const data = await api.getReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitReview(formData);
      setSuccess(true);
      setShowForm(false);
      setFormData({
        customer_name: '',
        customer_email: '',
        rating: 5,
        review_text: '',
        event_type: '',
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '5.0';

  return (
    <div className="reviews-page">
      <section className="reviews-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">Testimonials</span>
            <h1 className="page-title">Customer Reviews</h1>
            <p className="page-description">
              See what our clients have to say about their experience with Luxe Lens Events.
            </p>

            <div className="reviews-summary">
              <div className="summary-rating">
                <span className="rating-value">{averageRating}</span>
                <div className="rating-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className={i < Math.round(parseFloat(averageRating)) ? 'star-filled' : 'star-empty'} />
                  ))}
                </div>
                <span className="rating-count">Based on {reviews.length} reviews</span>
              </div>
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                Write a Review
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {success && (
        <div className="success-banner">
          <div className="container">
            Thank you for your review! It will be visible once approved.
          </div>
        </div>
      )}

      <section className="section">
        <div className="container">
          {loading ? (
            <div className="loading">
              <div className="spinner" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="empty-state">
              <p>No reviews yet. Be the first to share your experience!</p>
              <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                Write a Review
              </button>
            </div>
          ) : (
            <div className="reviews-grid">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  className="review-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="review-header">
                    <div className="review-avatar">
                      {review.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="review-name">{review.customer_name}</h4>
                      <span className="review-event">{review.event_type}</span>
                    </div>
                  </div>
                  <div className="review-stars">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className={i < review.rating ? 'star-filled' : 'star-empty'} />
                    ))}
                  </div>
                  <p className="review-text">"{review.review_text}"</p>
                  {review.event_date && (
                    <span className="review-date">{review.event_date}</span>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Review Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <motion.div
            className="modal-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Write a Review</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Your Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Event Type</label>
                <select
                  className="form-input form-select"
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  required
                >
                  <option value="">Select event type</option>
                  <option value="Wedding">Wedding</option>
                  <option value="Corporate Event">Corporate Event</option>
                  <option value="Birthday Party">Birthday Party</option>
                  <option value="Quinceañera">Quinceañera</option>
                  <option value="Holiday Party">Holiday Party</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Rating</label>
                <div className="rating-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="rating-star-btn"
                    >
                      <Star size={28} className={star <= formData.rating ? 'star-filled' : 'star-empty'} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Your Review</label>
                <textarea
                  className="form-input form-textarea"
                  rows={4}
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  placeholder="Tell us about your experience..."
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Review'}
                  <Send size={18} />
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
